import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, IonSelect } from '@ionic/angular';
import { combineLatest, startWith, Subject, Subscription } from 'rxjs';
import { IProfile } from 'src/app/appModels/IProfile';
import { IStore } from 'src/app/appModels/IStore';
import { GlobalAuthResolver } from 'src/app/appServices/global-auth/global-auth.resolver';
import { StoreResolver } from 'src/app/appServices/store/store.resolver';
import { TranslationService } from 'src/app/appServices/translation.service';

@Component({
  selector: 'app-select-store',
  templateUrl: './select-store.component.html',
  styleUrls: ['./select-store.component.scss'],
  standalone: false
})
export class SelectStoreComponent
  implements OnInit, AfterViewInit, OnDestroy {
  storeList!: IStore[];
  selectedStore!: IStore;

  destroy$: Subject<boolean> = new Subject<boolean>();
  @ViewChild('mySelect') selectRef!: IonSelect;

  storeOptions = {
    header: '',
    message: '',
    translucent: true,
    backdropDismiss: true,
  };
  okText = '';
  cancelText = '';
  profile!: IProfile;

  private subs: Subscription = new Subscription();

  constructor(
    private _storeResolver: StoreResolver,
    private _globalAuthResolver: GlobalAuthResolver,
    private alertController: AlertController,
    public translationService: TranslationService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    // console.log('select store: ngOninit called')
    // this.getProfile();
    // this.subscribeStore();
    this.updateStoreOptions();
    this.init();

  }

  ngAfterViewInit(): void {
    // console.log('select store: ngafterviewinit called');
    // this.subscribeChangeStoreClick();
  }


  init() {
    this.subs.add(
      combineLatest([
        this._storeResolver.storeList,
        this._globalAuthResolver.profile,
        this._globalAuthResolver.loggedIn,
        this._storeResolver.changeStoreClick.pipe(startWith(null))
      ]).subscribe(([storeList, profile, loggedIn, _]) => {
        // Never open store selector on auth routes or while logged out.
        if (!loggedIn || this.router.url.startsWith('/auth')) {
          void this.onStoreModalClose();
          return;
        }

        if (!storeList || !profile) return;

        this.storeList = storeList;
        this.profile = profile;

        if (this.storeList?.length == 1) {
          this.setSelectedStore(this.storeList[0]);
        } else {
          if (this.profile.role == 'Employee' || this.profile.role == 'Cashier') {
            const profileSpecificStore = this.storeList.find(
              (e) => e.id == this.profile.storeId
            );
            if (profileSpecificStore) {
              this.setSelectedStore(profileSpecificStore);
            }
          } else {
            this.onStoreModalOpen();
          }
        }
      })
    );
  }

  /**
   * Get Profile
   */
  getProfile() {
    this.subs.add(
      this._globalAuthResolver.profile.subscribe(
        (res: any) => {
          if (res) {
            this.profile = res;
          }
        }
      )
    );
  }

  /**
   * Subscribe store
   */
  subscribeStore() {
    this.subs.add(
      this._storeResolver.storeList.subscribe((res: any) => {
        if (res) {
          this.storeList = res;
        }
      })
    );
  }

  /**
   * Subscribe Change store click
   */
  subscribeChangeStoreClick() {
    this.subs.add(
      this._storeResolver.changeStoreClick.subscribe((res: any) => {
        if (this.storeList) {
          if (this.storeList?.length == 1) {
            this.setSelectedStore(this.storeList[0]);
          } else {
            if (this.profile.role == 'Employee' || this.profile.role == 'Cashier') {

              const profileSpecificStore = this.storeList.find(
                (e) => e.id == this.profile.storeId
              );

              if (profileSpecificStore) {
                this.setSelectedStore(profileSpecificStore);
              }

            } else {
              this.onStoreModalOpen();
            }
          }
        }
      })
    );
    this._storeResolver.changeStoreClick.next(null);
  }

  /**
   * Store modal open
   */
  async onStoreModalOpen() {
    await this.onStoreModalClose();
    if (!this.selectRef) {
      return;
    }
    await this.selectRef.open();
  }

  /**
   * Store modal close
   */
  async onStoreModalClose() {
    const alert = await this.alertController.getTop();
    if (alert) {
      alert.dismiss();
    }
  }

  /**
   * Compare with fn
   * @param o1
   * @param o2
   * @returns
   */
  compareWith(o1: any, o2: any) {
    return o1 && o2 ? o1 === o2 : o1 === o2;
  }

  /**
   * Set selected store
   * @param store
   */
  setSelectedStore(store: IStore) {
    this._storeResolver.setSelectedStore(store);
    // this._cartResolver.clearCart();
  }

  /**
   * Update store options with translated strings
   */
  private updateStoreOptions() {
    this.storeOptions.header = this.translationService.translate('SELECT_STORE');
    this.storeOptions.message = this.translationService.translate('CHOOSE_STORE_TO_CONTINUE');
    this.okText = this.translationService.translate('OK');
    this.cancelText = this.translationService.translate('CANCEL');
    
    // Subscribe to language changes to update options dynamically
    this.subs.add(
      this.translationService.currentLanguage$.subscribe(() => {
        this.storeOptions.header = this.translationService.translate('SELECT_STORE');
        this.storeOptions.message = this.translationService.translate('CHOOSE_STORE_TO_CONTINUE');
        this.okText = this.translationService.translate('OK');
        this.cancelText = this.translationService.translate('CANCEL');
      })
    );
  }

  ngOnDestroy(): void {
    // console.log('select store destroy');
    this.subs.unsubscribe();
  }
}
